import { useCallback, useMemo } from 'react';

import clsxm from '@/lib/clsxm';
import { useGlobalContext } from '@/hooks/use-global-context';

import Row from '@/components/row';
import Typography from '@/components/typography';

import { RowLayout } from '@/enums/row-layout';
import Carousel, { CarouselVariant } from '@/features/carousel';
import { PressCard } from '@/features/press/components/press-card';
import { PressItemProps } from '@/features/press/types';

export type PressListProps = {
  id: string;
  featured?: boolean;
  useLogo?: boolean;
  limit?: number;
  title?: string;
};

export const PressList = ({ id, title, useLogo, featured }: PressListProps) => {
  const { pageStaticData } = useGlobalContext();

  const pressRowLayout = useMemo(() => {
    if (featured && !useLogo) {
      return RowLayout.THREE_COLUMN;
    }

    if (featured && useLogo) {
      return RowLayout.TWO_COLUMN_HALFS;
    }

    return RowLayout.FULL_WIDE;
  }, [featured, useLogo]);

  const pressToPressCard = useCallback(
    (press: PressItemProps) => (
      <PressCard
        key={press.id}
        headline={press.headline}
        logoImage={press.logoImage}
        publishDate={press.publishDate}
        featured={featured}
        useLogo={useLogo}
        link={press.link}
        pressImage={press.pressImage || undefined}
      />
    ),
    [featured, useLogo]
  );

  if (
    !pageStaticData?.presses ||
    !pageStaticData?.presses[id] ||
    !pageStaticData.presses[id]?.length
  ) {
    return null;
  }

  const presses: PressItemProps[] = pageStaticData.presses[id];

  return (
    <>
      <div className='flex justify-center text-center'>
        <Typography.Heading1 className='mx-14 mb-8'>
          {title}
        </Typography.Heading1>
      </div>
      <Row
        layout={pressRowLayout}
        className={clsxm(
          'mx-0 justify-center px-0 lg:justify-start',
          featured && 'hidden lg:flex',
          !featured && 'gap-x-4 gap-y-8 md:grid md:grid-cols-2 lg:flex'
        )}
      >
        {presses.map(pressToPressCard)}
      </Row>

      {featured && (
        <div className='flex lg:hidden'>
          <Carousel
            variant={CarouselVariant.PAGINATED}
            items={presses.map((press) => ({
              id: press.id,
              component: (
                <div className='w-full p-2'>{pressToPressCard(press)}</div>
              ),
            }))}
            showPagination
            perPage={{
              sm: 1,
              md: 2,
            }}
          />
        </div>
      )}
    </>
  );
};
export default PressList;
